/**
 * Draft Workflow API Service
 *
 * Handles draft workflow operations:
 * - Saving drafts (with skipValidation)
 * - Fetching draft artifacts
 * - Discarding drafts
 * - Publishing drafts
 *
 * Like logicAppsApi.ts, this module routes to different backends per mode:
 *   Local:  Vite plugin at /local/workflows/{name}/draft (reads/writes disk directly)
 *   Cloud:  VFS at /admin/vfs/.../draft/ (HTTP file API on the ACA app)
 *
 * The isLocalMode() guards here follow the same authoring-split pattern.
 * See logicAppsApi.ts header for the full explanation and future consolidation notes.
 */

import type { ConnectionsConfiguration, ConnectionsResponse } from '@/types/logicApps';
import type { DraftSaveResult, PublishResult } from '@/types/draftMode';
import type { VFSFileMap } from '@/types/vfs';
import {
  isLocalMode,
  getLocalPluginUrl,
  localFetch,
} from './localRuntimeClient';
import { parseJsonBody } from './apiError';
import { fetchWorkflowFilesWithMetadata } from './vfsService';
import { clearMasterKeyCache } from './azureMasterKeyService';
import { telemetryService } from '@/services/telemetryService';
import { AppContext, appFetch, AppFetchError, getAppUrl, getNewVfsUrl } from './appClient';
import { apiVersions } from './endpoints';
import { deployArtifacts, unwrapLocalConnectionsResponse } from './logicAppsApi';
import { nonEmptyObject } from './workflowSerializer';
import { promoteDraftSettingsToProduction, getEnvironmentSettings, type EnvironmentSetting } from './environmentSettingsApi';
import { WorkflowDefinition } from '@/types';
// Re-export for backwards compatibility
export { clearMasterKeyCache };


// Draft writes always overwrite — `If-Match: *` skips the HEAD preflight that
// fetches ETags, avoiding 405 (HEAD not supported on workflow subdirs) and
// 412 (stale ETag from concurrent autosaves).
const VFS_OVERWRITE_HEADERS = { 'If-Match': '*' } as const;

/**
 * How long to wait after promoting environment settings into production before
 * issuing the deploy-artifacts call. Writing production settings restarts the
 * app; deploying while it is mid-restart can fail, so we give it a brief grace
 * period to come back online.
 */
const SETTINGS_PROMOTION_RESTART_DELAY_MS = 15000;

/**
 * How long to wait before retrying a deploy-artifacts call that failed with a
 * 502. A 502 usually means the app is still coming back online (e.g. right
 * after the settings promotion restarts it), so a short pause before the single
 * retry gives it time to become ready.
 */
const DEPLOY_502_RETRY_DELAY_MS = 5000;

/**
 * URLs of the four on-disk draft files for a workflow. Used by both
 * `saveDraftWorkflow` (writes drafts) and `publishDraft` (mirrors prod into
 * drafts post-publish) — kept in one place so the layout stays consistent.
 */
const draftFilePaths = (opts: AppContext & { workflowName: string }) => {
  const base = getNewVfsUrl(opts);
  const v = `?api-version=${apiVersions.otto}`;
  return {
    workflow: `${base}/${opts.workflowName}/workflow-draft.json${v}`,
    connections: `${base}/connections-draft.json${v}`,
    parameters: `${base}/parameters-draft.json${v}`,
    notes: `${base}/${opts.workflowName}/notes-draft.json${v}`,
  };
};


// =============================================================================
// SAVE DRAFT WORKFLOW
// =============================================================================

export type SaveDraftWorkflowOptions = AppContext & {
  workflowName: string;
  /** The complete workflow definition including kind */
  workflowDefinition: {
    definition: Record<string, unknown>;
    kind: string;
  };
  /** Optional connections configuration to save alongside workflow */
  connectionsConfig?: ConnectionsConfiguration | undefined;
  /** Optional parameters configuration to save alongside workflow */
  parametersConfig?: Record<string, unknown> | undefined;
  /** Optional notes configuration to save alongside workflow */
  notesConfig?: Record<string, unknown> | undefined;
  /** Skip server-side validation (default: true for drafts) */
  skipValidation?: boolean;
};

/**
 * Saves a workflow as a draft.
 * Draft files are saved with '-draft' suffix (e.g., workflow-draft.json).
 * Validation is skipped by default for draft saves.
 */
export const saveDraftWorkflow = async (opts: SaveDraftWorkflowOptions): Promise<DraftSaveResult> => {
  const start = performance.now();

  if (isLocalMode()) {
    // In local mode, save to draft files
    const workflowUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/draft`;
    await localFetch(workflowUrl, {
      method: 'POST',
      body: opts.workflowDefinition,
    });

    // Save draft connections if provided
    if (opts.connectionsConfig) {
      const connectionsUrl = `${getLocalPluginUrl()}/local/connections/draft`;
      await localFetch(connectionsUrl, {
        method: 'POST',
        body: opts.connectionsConfig,
      });
    }
    
    // Save draft parameters if provided
    if (opts.parametersConfig) {
      const parametersUrl = `${getLocalPluginUrl()}/local/parameters/draft`;
      await localFetch(parametersUrl, {
        method: 'POST',
        body: opts.parametersConfig,
      });
    }

    // Save draft notes if provided
    if (opts.notesConfig && Object.keys(opts.notesConfig).length > 0) {
      try {
        const notesUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/notes/draft`;
        await localFetch(notesUrl, {
          method: 'POST',
          body: opts.notesConfig,
        });
      } catch {
        // Notes endpoint may not exist in local plugin - not critical
        console.warn('[saveDraftWorkflow] Local notes endpoint not available');
      }
    }

    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveDraftWorkflow',
      method: 'POST',
      statusCode: '200',
    });
    return { success: true, savedAt: new Date() };
  }

  const accessToken = await opts.getToken();
  if (!accessToken) {
    const errorMessage = 'Failed to get access token';
    console.error('[saveDraftWorkflow] ' + errorMessage);
    return { success: false, error: errorMessage };
  }

  // In cloud mode, write each draft file individually via VFS PUT
  const paths = draftFilePaths(opts);
  const vfsPut = (body: string) => ({
    method: 'PUT' as const,
    headers: VFS_OVERWRITE_HEADERS,
    body,
  });

  const puts: Promise<unknown>[] = [
    appFetch(paths.workflow, accessToken, vfsPut(JSON.stringify(opts.workflowDefinition))),
  ];

  // Draft saves only write connections-draft.json. Draft runs resolve connections
  // from that file; production connections.json is updated exclusively on publish.
  if (opts.connectionsConfig) {
    puts.push(appFetch(paths.connections, accessToken, vfsPut(JSON.stringify(opts.connectionsConfig))));
  }

  if (opts.parametersConfig) {
    puts.push(appFetch(paths.parameters, accessToken, vfsPut(JSON.stringify(opts.parametersConfig))));
  }

  if (nonEmptyObject(opts.notesConfig)) {
    puts.push(appFetch(paths.notes, accessToken, vfsPut(JSON.stringify(opts.notesConfig))));
  }

  try {
    await Promise.all(puts);
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to save draft';
    telemetryService.trackEvent('api.error', {
      endpoint: 'saveDraftWorkflow',
      method: 'PUT',
      statusCode: 'unknown',
      error: errorMessage.slice(0, 200),
    });
    return { success: false, error: errorMessage };
  }

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'saveDraftWorkflow',
    method: 'POST',
    statusCode: '200',
  });
  return { success: true, savedAt: new Date() };
};

// =============================================================================
// GET DRAFT WORKFLOW
// =============================================================================

export type GetDraftWorkflowOptions = AppContext & {
  workflowName: string;
};

export type DraftWorkflowFiles = {
  workflow: { definition: Record<string, unknown>; kind?: string } | null;
  connections: ConnectionsConfiguration | null;
  parameters: Record<string, unknown> | null;
  notes: Record<string, unknown> | null;
  /** VFS file metadata for timestamp comparison (stale draft detection) */
  vfsFiles: VFSFileMap | null;
};

/**
 * Fetches draft workflow artifacts.
 * Returns null for artifacts that don't exist.
 *
 * In cloud mode, uses VFS service to fetch files with metadata (including mtime
 * timestamps needed for stale draft detection).
 */
export const getDraftWorkflow = async (opts: GetDraftWorkflowOptions): Promise<DraftWorkflowFiles> => {
  if (isLocalMode()) {
    // In local mode, fetch draft files (no VFS timestamp support)
    try {
      const workflowUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/draft`;
      const workflow = await localFetch<{ definition: Record<string, unknown>; kind?: string }>(workflowUrl);

      let connections: ConnectionsConfiguration | null = null;
      try {
        const connectionsUrl = `${getLocalPluginUrl()}/local/connections/draft`;
        const connectionsResponse = await localFetch<
          ConnectionsResponse | ConnectionsConfiguration
        >(connectionsUrl);
        // The local plugin returns the ARM envelope
        // ({ properties: { files: { 'connections.json': ... } } }); unwrap it to
        // the bare config so references hydrate correctly (empty otherwise).
        connections = unwrapLocalConnectionsResponse(connectionsResponse);
      } catch {
        // Draft connections may not exist
      }

      let notes: Record<string, unknown> | null = null;
      try {
        const notesUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/notes/draft`;
        notes = await localFetch<Record<string, unknown>>(notesUrl);
      } catch {
        // Draft notes may not exist
      }

      return { workflow, connections, parameters: null, notes, vfsFiles: null };
    } catch {
      // Draft doesn't exist
      return { workflow: null, connections: null, parameters: null, notes: null, vfsFiles: null };
    }
  }

  // In cloud mode, use VFS service to fetch all workflow files with metadata
  // This provides mtime timestamps needed for stale draft detection
  try {
    const vfsFiles = await fetchWorkflowFilesWithMetadata(opts);

    // Extract draft files from VFS response
    const workflow = vfsFiles['workflow-draft.json']?.data as { definition: Record<string, unknown>; kind?: string } | null ?? null;
    const connections = vfsFiles['connections-draft.json']?.data as ConnectionsConfiguration | null ?? null;
    const parameters = vfsFiles['parameters-draft.json']?.data as Record<string, unknown> | null ?? null;
    const notes = vfsFiles['notes-draft.json']?.data as Record<string, unknown> | null ?? null;

    return { workflow, connections, parameters, notes, vfsFiles };
  } catch (error) {
    console.warn('[getDraftWorkflow] Failed to fetch via VFS, falling back to direct fetch:', error);
  }

  return { workflow: null, connections: null, parameters: null, notes: null, vfsFiles: null };
};

// =============================================================================
// DISCARD DRAFT
// =============================================================================

export type DiscardDraftOptions = AppContext & {
  workflowName: string;
  /** Production workflow to overwrite draft with */
  prodWorkflow?: {
    definition: Record<string, unknown>;
    kind: string;
  } | undefined;
  /** Production connections to overwrite draft with */
  prodConnections?: ConnectionsConfiguration | undefined;
  /** Production notes to overwrite draft with */
  prodNotes?: Record<string, unknown> | undefined;
};

/**
 * Discards draft artifacts by deleting draft files.
 *
 * In cloud mode, deletes draft files via VFS DELETE (ignoring 404s).
 * In local mode, overwrites draft files with production content.
 */
export const discardDraft = async (opts: DiscardDraftOptions): Promise<DraftSaveResult> => {
  const { prodWorkflow, prodConnections } = opts;
  
  if (isLocalMode()) {
    // In local mode, overwrite draft files with production content
    if (prodWorkflow) {
      try {
        const workflowUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/draft`;
        await localFetch(workflowUrl, {
          method: 'POST',
          body: prodWorkflow,
        });
      } catch (err) {
        console.warn('[discardDraft] Failed to overwrite workflow draft:', err);
      }
    }
    
    if (prodConnections) {
      try {
        const connectionsUrl = `${getLocalPluginUrl()}/local/connections/draft`;
        await localFetch(connectionsUrl, {
          method: 'POST',
          body: prodConnections,
        });
      } catch (err) {
        console.warn('[discardDraft] Failed to overwrite connections draft:', err);
      }
    }

    if (opts.prodNotes && Object.keys(opts.prodNotes).length > 0) {
      try {
        const notesUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/notes/draft`;
        await localFetch(notesUrl, {
          method: 'POST',
          body: opts.prodNotes,
        });
      } catch (err) {
        console.warn('[discardDraft] Failed to overwrite notes draft:', err);
      }
    }

    return { success: true };
  }

  // In cloud mode, delete draft files via VFS
  const paths = draftFilePaths(opts);
  const deletePaths = [paths.workflow, paths.connections, paths.parameters, paths.notes];

  const accessToken = await opts.getToken();
  if (!accessToken) {
    const errorMessage = 'Failed to get access token';
    console.error('[discardDraft] ' + errorMessage);
    return { success: false, error: errorMessage };
  }

  await Promise.all(
    deletePaths.map(async (path) => {
      try {
        await appFetch(path, accessToken, { method: 'DELETE', headers: VFS_OVERWRITE_HEADERS });
      } catch {
        // Ignore errors (e.g., 404 if draft file doesn't exist)
      }
    }),
  );

  return { success: true };
};

// =============================================================================
// PUBLISH DRAFT
// =============================================================================

export type PublishDraftOptions = AppContext & {
  workflowName: string;
  /** The draft workflow definition to publish */
  workflowDefinition: {
    definition: Record<string, unknown>;
    kind: string;
  };
  /** Optional connections configuration */
  connectionsConfig?: ConnectionsConfiguration | undefined;
  /** Optional parameters configuration */
  parametersConfig?: Record<string, unknown> | undefined;
  /** Optional notes configuration */
  notesConfig?: Record<string, unknown> | undefined;
  /** Location for validation endpoint (e.g., 'eastus') */
  location?: string | undefined;
  /** Skip validation before publish (not recommended) */
  skipValidation?: boolean | undefined;
  /**
   * Whether the workflow is being authored in draft mode. Controls which
   * environment-settings slot (draft vs production) is read for validation.
   * The draft slot is only used when the `enableDraftSettings` flag is on
   * (enforced by `getEnvironmentSettings`).
   */
  isDraftMode?: boolean | undefined;
};

/**
 * Flattens an environment-settings collection into the `{ key: value }` map
 * the validate endpoint expects under `properties.appSettings.values`.
 * Null values are normalized to empty strings. Uses `Object.fromEntries` so a
 * setting named `__proto__` becomes an own data property instead of invoking
 * the prototype setter (which would silently drop it).
 */
const toAppSettingValues = (settings: EnvironmentSetting[]): Record<string, string> =>
  Object.fromEntries(settings.map(({ name, value }) => [name, value ?? '']));

/**
 * Validates a workflow before publishing.
 * Uses the Azure Management API validate endpoint.
 */
export const validateWorkflow = async (opts: PublishDraftOptions): Promise<{ isValid: boolean; error?: string }> => {
  const validationUrl = `${getAppUrl(opts)}/workflows/${opts.workflowName}/validate?api-version=${apiVersions.otto}`;

  // Include connections so the backend resolves references against the in-progress
  // draft state (not what's currently committed to connections.json on disk).
  const validationBody: { properties: Record<string, unknown> } = {
    properties: {
      definition: opts.workflowDefinition.definition,
      kind: opts.workflowDefinition.kind,
    },
  };
  const connectionsForValidation = nonEmptyObject(opts.connectionsConfig);
  if (connectionsForValidation) {
    validationBody.properties.connections = connectionsForValidation;
  }

  try {
    const accessToken = await opts.getToken();
    if (!accessToken) {
      const errorMessage = 'Failed to get access token for validation';
      console.error('[validateWorkflow] ' + errorMessage);
      return { isValid: true }; // Allow publish to proceed if we can't validate
    }

    // Include app settings so the backend resolves `@appsetting()` references
    // against the slot the workflow will run with. The draft vs production slot
    // is chosen by `isDraftMode` and gated by the `enableDraftSettings` flag
    // inside `getEnvironmentSettings`. Non-fatal: validate without them if the
    // read fails.
    try {
      const settings = await getEnvironmentSettings(
        opts.projectName,
        opts.appName,
        opts.isDraftMode ?? false,
        accessToken,
      );
      const values = toAppSettingValues(settings);
      if (Object.keys(values).length > 0) {
        validationBody.properties.appSettings = { values };
      }
    } catch (settingsError) {
      console.warn('[validateWorkflow] Could not load app settings for validation:', settingsError);
    }

    await appFetch(validationUrl, accessToken, {
      method: 'POST',
      body: JSON.stringify(validationBody),
    });

    return { isValid: true };
  } catch (error) {
    // 404 = workflow doesn't exist yet (valid for new workflows)
    if (error instanceof AppFetchError && error.status === 404) {
      return { isValid: true };
    }

    let errorMessage = error instanceof Error ? error.message : 'Validation failed';

    // Extract structured error message from AppFetchError body
    if (error instanceof AppFetchError && error.body) {
      const errorJson = parseJsonBody(error.body);
      if (typeof errorJson === 'string') {
        errorMessage = errorJson;
      } else if (errorJson && typeof errorJson === 'object') {
        const obj = errorJson as { error?: { message?: string }; message?: string };
        errorMessage = obj.error?.message || obj.message || errorMessage;
      }
    }

    console.error('[validateWorkflow] Validation failed:', errorMessage);
    // If validation request itself fails due to network, allow publish to proceed
    if (error instanceof Error && !(error instanceof AppFetchError) && (error.message.includes('fetch') || error.message.includes('network'))) {
      return { isValid: true };
    }
    return { isValid: false, error: errorMessage };
  }
};

/**
 * Publishes a draft by saving it as the production workflow.
 *
 * 1. Validate workflow (unless skipValidation is true)
 * 2. Deploy production files via deployWorkflowArtifacts
 * 3. Mirror the just-published prod content into the draft files so drafts
 *    remain visible (matching the last published state) until the user makes
 *    a new edit. Sibling configs absent in prod are deleted from drafts so
 *    drafts stay in sync.
 */
export const publishDraft = async (opts: PublishDraftOptions): Promise<PublishResult> => {
  const start = performance.now();
  const { skipValidation = false } = opts;
  
  if (isLocalMode()) {
    // In local mode, save to production files
    const workflowUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}`;
    await localFetch(workflowUrl, {
      method: 'POST',
      body: opts.workflowDefinition,
    });

    // Save production connections if provided
    if (opts.connectionsConfig) {
      const connectionsUrl = `${getLocalPluginUrl()}/local/connections`;
      await localFetch(connectionsUrl, {
        method: 'POST',
        body: opts.connectionsConfig,
      });
    }
    
    // Save production parameters if provided
    if (opts.parametersConfig) {
      const parametersUrl = `${getLocalPluginUrl()}/local/parameters`;
      await localFetch(parametersUrl, {
        method: 'POST',
        body: opts.parametersConfig,
      });
    }

    // Save production notes if provided
    if (opts.notesConfig && Object.keys(opts.notesConfig).length > 0) {
      try {
        const notesUrl = `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/notes`;
        await localFetch(notesUrl, {
          method: 'POST',
          body: opts.notesConfig,
        });
      } catch {
        console.warn('[publishDraft] Local notes endpoint not available');
      }
    }

    // Discard draft after successful publish (overwrite with prod content)
    await discardDraft({
      ...opts,
      prodWorkflow: opts.workflowDefinition,
      prodConnections: opts.connectionsConfig,
      prodNotes: opts.notesConfig,
    });

    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'publishDraft',
      method: 'POST',
      statusCode: '200',
    });
    return { success: true };
  }

  // Step 1: Validate before publish (unless skipped)
  if (!skipValidation) {
    const validationResult = await validateWorkflow(opts);
    if (!validationResult.isValid) {
      return { 
        success: false, 
        error: validationResult.error || 'Validation failed',
      };
    }
  }

  // Step 2: Resolve the access token used by the remaining steps.
  const accessToken = await opts.getToken();
  if (!accessToken) {
    const errorMessage = 'Failed to get access token';
    console.error('[publishDraft] ' + errorMessage);
    return { success: false, error: errorMessage };
  }

  // Step 3: Promote draft environment settings into production BEFORE deploying
  // the workflow artifacts. Connection secrets (`connectionreference_*` app
  // settings) are authored against the draft slot while editing; the production
  // slot must carry them before the workflow that references them via
  // `@appsetting()` goes live, otherwise the just-deployed workflow could run
  // against missing settings. No-op when draft settings aren't enabled for the
  // app. Merge is a pure union (never deletes); draft wins only for
  // connection-reference keys.
  try {
    const settingsPromoted = await promoteDraftSettingsToProduction(
      opts.projectName,
      opts.appName,
      accessToken,
    );

    // Writing production settings restarts the app. Give it a moment to become
    // ready again before issuing the deploy-artifacts call below, otherwise the
    // deploy can hit the app mid-restart and fail. Only wait when settings were
    // actually written (no-op promotions leave the app untouched).
    if (settingsPromoted) {
      await new Promise((resolve) => setTimeout(resolve, SETTINGS_PROMOTION_RESTART_DELAY_MS));
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to promote draft settings';
    telemetryService.trackEvent('api.error', {
      endpoint: 'publishDraft.promoteSettings',
      method: 'PUT',
      statusCode: 'unknown',
      error: errorMessage.slice(0, 200),
    });
    return { success: false, error: errorMessage };
  }

  // Step 4: Deploy production files via deploy artifacts.
  // Pass rethrow502 so a 502 surfaces here instead of being swallowed — the app
  // may still be warming up after the settings promotion above, so we wait and
  // retry the deploy once before giving up.
  const deploy = () =>
    deployArtifacts(
      `${getAppUrl(opts)}/deployWorkflowArtifacts?api-version=${apiVersions.otto}`,
      opts,
      { [opts.workflowName]: opts.workflowDefinition as WorkflowDefinition },
      opts.connectionsConfig,
      opts.parametersConfig,
      nonEmptyObject(opts.notesConfig),
      /* rethrow502 */ true,
    );

  try {
    try {
      await deploy();
    } catch (err) {
      // A 502 usually means the app is still coming back online (e.g. it just
      // restarted after the settings promotion above). Wait a few seconds and
      // retry the deploy once before giving up.
      if (err instanceof AppFetchError && err.status === 502) {
        await new Promise((resolve) => setTimeout(resolve, DEPLOY_502_RETRY_DELAY_MS));
        await deploy();
      } else {
        throw err;
      }
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to publish';
    telemetryService.trackEvent('api.error', {
      endpoint: 'publishDraft',
      method: 'PUT',
      statusCode: 'unknown',
      error: errorMessage.slice(0, 200),
    });
    return { success: false, error: errorMessage };
  }

  // Step 5: Mirror prod into drafts so the drafts view never appears empty
  // post-publish. PUT non-empty configs, DELETE absent ones — drafts stay
  // in sync with what was actually published.
  const paths = draftFilePaths(opts);
  const mirrors: Array<{ path: string; content: object | undefined }> = [
    { path: paths.workflow, content: opts.workflowDefinition },
    { path: paths.connections, content: nonEmptyObject(opts.connectionsConfig) },
    { path: paths.parameters, content: nonEmptyObject(opts.parametersConfig) },
    { path: paths.notes, content: nonEmptyObject(opts.notesConfig) },
  ];

  await Promise.all(
    mirrors.map(async ({ path, content }) => {
      try {
        await appFetch(
          path,
          accessToken,
          content
            ? { method: 'PUT', headers: VFS_OVERWRITE_HEADERS, body: JSON.stringify(content) }
            : { method: 'DELETE', headers: VFS_OVERWRITE_HEADERS },
        );
      } catch {
        // Best-effort — drafts will rehydrate on next workflow load if needed.
      }
    }),
  );

  telemetryService.trackMetric('api.response_time', performance.now() - start, {
    endpoint: 'publishDraft',
    method: 'POST',
    statusCode: '200',
  });
  return { success: true };
};
